// frontend-react/src/components/BoxScore/DateNavigation.js
// Date navigation for box score display

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { formatDateForDisplay, isToday } from '../../services/boxScoreApi';

/**
 * DateNavigation Component
 * Allows users to navigate between dates with previous/next buttons and date picker
 * 
 * @param {string} currentDate - Current selected date (YYYY-MM-DD)
 * @param {string[]} availableDates - Array of available dates (YYYY-MM-DD)
 * @param {function} onDateChange - Callback when date changes
 * @param {boolean} loading - Whether data is loading
 */
const DateNavigation = ({ currentDate, availableDates = [], onDateChange, loading = false }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (showDatePicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.right - 256 // 256px = w-64
      });
    }
  }, [showDatePicker]);

  // Find current date index in available dates
  const currentIndex = availableDates.indexOf(currentDate);
  const hasPrevious = currentIndex < availableDates.length - 1;
  const hasNext = currentIndex > 0;

  const handlePrevious = () => {
    if (hasPrevious && !loading) {
      const previousDate = availableDates[currentIndex + 1];
      onDateChange(previousDate);
    }
  };

  const handleNext = () => {
    if (hasNext && !loading) {
      const nextDate = availableDates[currentIndex - 1];
      onDateChange(nextDate);
    }
  };

  const handleToday = () => {
    if (!loading) {
      const today = new Date().toISOString().split('T')[0];
      // Find closest available date to today
      const closestDate = availableDates.find(date => date <= today) || availableDates[0];
      if (closestDate) {
        onDateChange(closestDate);
      }
    }
  };

  const handleDateSelect = (date) => {
    if (!loading) {
      onDateChange(date);
      setShowDatePicker(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4">
      {/* Date Display and Navigation */}
      <div className="flex items-center gap-3">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          disabled={!hasPrevious || loading}
          className={`p-2 rounded-lg ${dynastyTheme.classes.transition} ${
            hasPrevious && !loading
              ? `${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-700 ${dynastyTheme.classes.text.primary}`
              : `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight} opacity-50 cursor-not-allowed`
          }`}
          title="Previous Day"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Current Date Display */}
        <div className={`px-6 py-2 rounded-lg ${dynastyTheme.classes.bg.darkLighter} min-w-[200px] text-center`}>
          <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-1`}>
            Date
          </div>
          <div className={`text-lg font-bold ${dynastyTheme.classes.text.white}`}>
            {formatDateForDisplay(currentDate)}
          </div>
          {isToday(currentDate) && (
            <div className={`text-xs ${dynastyTheme.classes.text.primary} mt-1`}>
              Today
            </div>
          )}
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={!hasNext || loading}
          className={`p-2 rounded-lg ${dynastyTheme.classes.transition} ${
            hasNext && !loading
              ? `${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-700 ${dynastyTheme.classes.text.primary}`
              : `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight} opacity-50 cursor-not-allowed`
          }`}
          title="Next Day"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Quick Actions */}
      <div className="flex items-center gap-3">
        {/* Date Picker Dropdown */}
        <div>
          <button
            ref={buttonRef}
            onClick={() => setShowDatePicker(!showDatePicker)}
            disabled={loading}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${dynastyTheme.classes.transition} ${
              loading
                ? `${dynastyTheme.classes.bg.darkLighter} opacity-50 cursor-not-allowed`
                : `${dynastyTheme.classes.bg.darkLighter} hover:bg-neutral-700 ${dynastyTheme.classes.text.white}`
            }`}
            title="Pick Date"
          >
            <CalendarIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Pick Date</span>
          </button>

          {/* Date Picker Dropdown Menu - Rendered via Portal */}
          {showDatePicker && ReactDOM.createPortal(
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0"
                style={{ zIndex: 99998 }}
                onClick={() => setShowDatePicker(false)}
              />

              {/* Dropdown */}
              <div
                className={`fixed w-64 max-h-96 overflow-y-auto rounded-lg shadow-xl ${dynastyTheme.classes.bg.dark} border ${dynastyTheme.classes.border.light}`}
                style={{
                  zIndex: 99999,
                  top: dropdownPosition.top,
                  left: dropdownPosition.left
                }}
              >
                <div className={`p-2 border-b ${dynastyTheme.classes.border.light} sticky top-0 ${dynastyTheme.classes.bg.dark}`}>
                  <div className={`text-sm font-medium ${dynastyTheme.classes.text.white}`}>
                    Select Date
                  </div>
                  <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
                    {availableDates.length} dates available
                  </div>
                </div>

                <div className="p-2 space-y-1">
                  {availableDates.map((date) => {
                    const isSelected = date === currentDate;
                    const isTodayDate = isToday(date);

                    return (
                      <button
                        key={date}
                        onClick={() => handleDateSelect(date)}
                        className={`w-full text-left px-3 py-2 rounded ${dynastyTheme.classes.transition} ${
                          isSelected
                            ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                            : `hover:bg-neutral-800 ${dynastyTheme.classes.text.white}`
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">
                            {formatDateForDisplay(date)}
                          </span>
                          {isTodayDate && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              isSelected
                                ? 'bg-black/20 text-black'
                                : `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark}`
                            }`}>
                              Today
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>,
            document.body
          )}
        </div>

        {/* Today Button */}
        <button
          onClick={handleToday}
          disabled={loading}
          className={`px-4 py-2 rounded-lg font-medium ${dynastyTheme.classes.transition} ${
            loading
              ? `${dynastyTheme.classes.bg.darkLighter} opacity-50 cursor-not-allowed`
              : `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.dark} hover:bg-yellow-500`
          }`}
          title="Jump to Today"
        >
          Today
        </button>
      </div>
    </div>
  );
};

export default DateNavigation;
